import * as Sentry from '@sentry/browser';
import axios from 'axios';
import moment from 'moment';

import { post } from '../api/api';

import { AWS, FILES_BUCKET } from '../config/aws-config';

type UploadParams = {
  dateTime: Date;
  dateTimeString: string;
  policyEncoded: string;
  signature: string;
};

export type OrganizationFileResult = {
  uploadLink: string;
  organizationFileId: number;
};

// "Disallowed" is a strong word, but generally recommended to avoid these
// https://docs.aws.amazon.com/AmazonS3/latest/userguide/object-keys.html
export const S3_DISALLOWED_CHARACTER_REGEX = /[{}/&$@=;:+,?\\^%`[\]~<>|#]/g;

const generateUploadRequestBody = (uploadParams: UploadParams) => {
  const formData = new FormData();
  formData.append('x-amz-server-side-encryption', 'AES256');
  formData.append(
    'X-Amz-Credential',
    `${AWS.ACCESS_ID}/${moment.utc(uploadParams.dateTime).format('YYYYMMDD')}/${
      AWS.REGION
    }/s3/aws4_request`,
  );
  formData.append('X-Amz-Algorithm', 'AWS4-HMAC-SHA256');
  formData.append('acl', 'public-read');
  formData.append('X-Amz-Date', uploadParams.dateTimeString);
  formData.append('Policy', uploadParams.policyEncoded);
  formData.append('X-Amz-Signature', uploadParams.signature);
  return formData;
};

export const uploadFile = (
  params: UploadParams,
  bucket: string,
  fileKey: string,
  file: File | Blob,
) => {
  const body = generateUploadRequestBody(params);
  body.append('key', fileKey);
  body.append('content-type', file.type);
  body.append('file', file);
  return axios.post(`https://${bucket}.s3.amazonaws.com/`, body);
};

export const uploadFileViaEmployer = (
  fileKey: string,
  file: File,
  bucket: string = FILES_BUCKET,
  acl: 'public-read' | undefined = undefined,
) => {
  const formData = new FormData();
  formData.append('file', file);
  return post(
    `/files?bucket=${bucket}&fileKey=${fileKey}&contentType=${file.type}${
      acl ? `&acl=${acl}` : ''
    }`,
    { body: formData, isMultipartUpload: true },
  );
};

export const uploadToFileBucketViaPresignedUrl = async (file: File, orgId: number) =>
  getPresignedUploadUrlViaEmployer(file, FILES_BUCKET, orgId).then(async (response: any) => {
    const result = await response.json();
    const res = await fetch(result.presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    // if we fail to get a response, we return null. Downstream functions should handle if there is no upload link
    if (!res.ok) {
      Sentry.captureMessage(
        `Unable to upload file to S3 using upload key. File name: ${file.name}, OrgId: ${orgId}.`,
      );
      return null;
    }

    // update status and get shortened link
    const statusUpdateResponse = await post(`/files/${result.organizationFileId}/status`).then(
      (x) => x.json(),
    );

    if (statusUpdateResponse && statusUpdateResponse.genericUrl) {
      return {
        uploadLink: statusUpdateResponse.genericUrl,
        organizationFileId: result.organizationFileId,
      } as OrganizationFileResult;
    }
    return null;
  });

const getPresignedUploadUrlViaEmployer = (file: File, bucket: string, orgId: number) =>
  post('/files?isPresignedUrl=true', {
    body: JSON.stringify({ fileName: file.name, bucket, orgId, contentType: file.type }),
  });
